/**
 * InvoiceDetailModal
 * Modal for viewing invoice details with actions (validate, download, view PDF)
 * Similar to the "Vérifier la facture" step in GenerateSingleInvoiceModal
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  PaperAirplaneIcon,
  EyeIcon,
  EnvelopeIcon,
  BuildingLibraryIcon,
  GlobeAltIcon,
  BanknotesIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useValidateInvoice, useCancelInvoice, useMarkInvoiceAsPaid, useInvoicePayments, useResyncFalco } from '../../hooks/useInvoices';
import { useValidateVat } from '../../hooks/useVatValidation';
import { downloadInvoicePdf, previewInvoicePdf, getEmailPreview } from '../../api/facturation.api';
import { useAppStore } from '@/app/store';
import { PdfPreviewModal } from '../PdfPreviewModal/PdfPreviewModal';
import { VatStatusSection } from '../GenerateInvoice/components/VatStatusSection';
import type { Invoice, InvoiceStatus, VatValidation } from '../../types/facturation.types';
import { formatDateShort } from '../../utils/dateFormat';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import '../../facturation.css';

// Language options for email
const LANGUAGE_OPTIONS = [
  { value: 'fr', label: 'FR' },
  { value: 'nl', label: 'NL' },
  { value: 'en', label: 'EN' },
  { value: 'de', label: 'DE' },
] as const;

// Status configuration
const STATUS_CONFIG: Record<InvoiceStatus, { label: string; className: string }> = {
  DRAFT: { label: 'Brouillon', className: 'draft' },
  GENERATED: { label: 'Genere', className: 'generated' },
  SENT: { label: 'Envoye', className: 'sent' },
  PAID: { label: 'Paye', className: 'paid' },
  CANCELLED: { label: 'Annule', className: 'cancelled' },
};

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

// Format date
const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return format(date, 'd MMMM yyyy', { locale: fr });
  } catch {
    return dateString;
  }
};

interface InvoiceDetailModalProps {
  invoice: Invoice;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  invoice,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [sendEmailOnValidate, setSendEmailOnValidate] = useState(true);

  // Email editor state
  const [emailLanguage, setEmailLanguage] = useState(invoice.language || 'fr');
  const [emailRecipient, setEmailRecipient] = useState(invoice.billing_email || '');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Track whether the user has manually edited the message
  const [userEditedMessage, setUserEditedMessage] = useState(false);

  const [vatCheckResult, setVatCheckResult] = useState<VatValidation | null>(null);

  const validateMutation = useValidateInvoice();
  const cancelMutation = useCancelInvoice();
  const markPaidMutation = useMarkInvoiceAsPaid();
  const resyncFalcoMutation = useResyncFalco();
  const validateVatMutation = useValidateVat();
  const addNotification = useAppStore((s) => s.addNotification);

  // Fetch linked payments when invoice is PAID or SENT
  const isPaidOrSent = invoice.status === 'PAID' || invoice.status === 'SENT';
  const { data: payments, isLoading: paymentsLoading } = useInvoicePayments(invoice.id, isPaidOrSent);

  const isDraft = invoice.status === 'DRAFT';
  const isGenerated = invoice.status === 'GENERATED';
  const isSent = invoice.status === 'SENT';
  const canValidate = isDraft || isGenerated;
  const canCancel = isDraft || isGenerated;
  const canMarkPaid = isSent;

  // Fetch email preview template from API
  const fetchEmailPreview = useCallback(async (lang: string) => {
    setIsLoadingPreview(true);
    setPreviewError(null);
    try {
      const result = await getEmailPreview(invoice.id, lang);
      if (result?.data) {
        setEmailSubject(result.data.subject);
        // Only update message if user hasn't manually edited it
        if (!userEditedMessage) {
          setEmailMessage(result.data.body);
        }
        if (result.data.recipient && !emailRecipient) {
          setEmailRecipient(result.data.recipient);
        }
      }
    } catch (err) {
      console.error('Failed to fetch email preview:', err);
      setPreviewError('Impossible de charger le modele email');
    } finally {
      setIsLoadingPreview(false);
    }
  }, [invoice.id, userEditedMessage, emailRecipient]);

  // Fetch preview when email checkbox is enabled or language changes
  useEffect(() => {
    if (sendEmailOnValidate && canValidate) {
      fetchEmailPreview(emailLanguage);
    }
  }, [sendEmailOnValidate, emailLanguage, canValidate, fetchEmailPreview]);

  // Handle language change — reset user edit flag so template refreshes
  const handleLanguageChange = (newLang: string) => {
    setEmailLanguage(newLang);
    setUserEditedMessage(false);
  };

  // Get affaire reference from invoice
  const getAffaireRef = (): string => {
    if (invoice.affaires?.length && invoice.affaires.length > 0) {
      return invoice.affaires[0].k_id_dossier || invoice.affaires[0].affaire_id;
    }
    return '-';
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      // Use preview endpoint for drafts (generates PDF on-the-fly with watermark)
      // Use download endpoint for finalized invoices (serves stored PDF)
      const blob = isDraft
        ? await previewInvoicePdf(invoice.id)
        : await downloadInvoicePdf(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const a = globalThis.document.createElement('a');
      a.href = url;
      const fileName = invoice.invoice_number
        ? `facture_${invoice.invoice_number.replace(/\//g, '-')}.pdf`
        : `facture_brouillon_${invoice.id}.pdf`;
      a.download = fileName;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle validate
  const handleValidate = async () => {
    setIsValidating(true);
    try {
      await validateMutation.mutateAsync({
        id: invoice.id,
        currentStatus: invoice.status,
        sendEmail: sendEmailOnValidate,
        ...(sendEmailOnValidate && {
          emailMessage: emailMessage || undefined,
          emailRecipient: emailRecipient || undefined,
          language: emailLanguage,
        }),
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Validate failed:', error);
    } finally {
      setIsValidating(false);
    }
  };

  // Handle cancel
  const handleCancel = async () => {
    const reason = prompt("Raison de l'annulation:");
    if (reason) {
      try {
        await cancelMutation.mutateAsync({
          id: invoice.id,
          reason,
          currentStatus: invoice.status,
        });
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error('Cancel failed:', error);
      }
    }
  };

  // Handle VAT re-verification against VIES
  const handleVerifyVat = () => {
    if (!invoice.billing_vat_number) return;
    let vatNumber = invoice.billing_vat_number.replace(/[.\s-]/g, '').toUpperCase();
    if (!/^[A-Z]{2}/.test(vatNumber)) {
      if (/^\d{10}$/.test(vatNumber)) vatNumber = 'BE' + vatNumber;
      else if (/^\d{9}$/.test(vatNumber)) vatNumber = 'BE0' + vatNumber;
    }
    validateVatMutation.mutate(
      {
        vatNumber,
        storedName: invoice.billing_name || undefined,
        storedAddress: invoice.billing_address || undefined,
      },
      {
        onSuccess: (result) => setVatCheckResult(result),
        onError: (err) => {
          console.error('VAT re-verification failed:', err);
          addNotification({
            type: 'error',
            title: 'Verification TVA echouee',
            message: err instanceof Error ? err.message : 'Impossible de joindre VIES.',
          });
        },
      },
    );
  };

  // Handle Falco resync
  const handleResyncFalco = async () => {
    try {
      await resyncFalcoMutation.mutateAsync({
        id: invoice.id,
        currentStatus: invoice.status,
      });
      addNotification({ type: 'success', title: 'Resync Falco lancee', message: 'La facture est en cours de transmission a Falco.' });
      onSuccess?.();
    } catch (error) {
      console.error('Resync Falco failed:', error);
      const message = error instanceof Error ? error.message : 'Echec de la resynchronisation avec Falco.';
      addNotification({ type: 'error', title: 'Resync Falco echouee', message });
    }
  };

  // Handle mark as paid
  const handleMarkPaid = async () => {
    if (confirm('Marquer cette facture comme payee ?')) {
      try {
        await markPaidMutation.mutateAsync({
          id: invoice.id,
          currentStatus: invoice.status,
        });
        addNotification({ type: 'success', title: 'Facture marquee comme payee' });
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error('Mark paid failed:', error);
        addNotification({ type: 'error', title: 'Erreur', message: 'Impossible de marquer la facture comme payee.' });
      }
    }
  };

  return (
    <>
      <Transition show={isOpen} as={React.Fragment}>
        <Dialog
          onClose={() => {
            if (!showPdfPreview) {
              onClose();
            }
          }}
          className="relative z-50"
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform rounded-2xl bg-white shadow-xl transition-all flex flex-col">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <DocumentTextIcon className="h-6 w-6 text-primary" />
                      <div>
                        <Dialog.Title className="text-lg font-semibold text-gray-900">
                          {isDraft ? 'Verifier le brouillon' : 'Details de la facture'}
                        </Dialog.Title>
                        <p className="text-sm text-gray-500">
                          {invoice.invoice_number || 'Brouillon'} • Dossier {getAffaireRef()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="facturation-btn-modal-close"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-6 overflow-y-auto flex-1">
                    {/* Draft warning */}
                    {isDraft && (
                      <div className="fact-draft-warning">
                        <p>
                          <strong>Brouillon</strong> - Le numero de facture sera attribue lors de la validation.
                        </p>
                      </div>
                    )}

                    {/* Status badge */}
                    <div className="flex items-center gap-3">
                      <span className={`fact-status-badge ${STATUS_CONFIG[invoice.status].className}`}>
                        {STATUS_CONFIG[invoice.status].label}
                      </span>
                      {invoice.validated_by && invoice.validated_at && (
                        <span className="text-xs text-gray-500">
                          Valide par {invoice.validator_first_name} {invoice.validator_last_name} le {formatDateShort(invoice.validated_at)}
                        </span>
                      )}
                    </div>

                    {/* Invoice summary */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Resume</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Numero:</span>
                          <span className="font-mono font-medium">{invoice.invoice_number || 'Brouillon'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Client:</span>
                          <span>{invoice.billing_name}</span>
                        </div>
                        {invoice.billing_vat_number && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">TVA Client:</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono">{invoice.billing_vat_number}</span>
                              <button
                                onClick={handleVerifyVat}
                                disabled={validateVatMutation.isPending}
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="Verifier le numero de TVA aupres de VIES"
                              >
                                {validateVatMutation.isPending ? (
                                  <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500"></div>
                                    Verification...
                                  </>
                                ) : (
                                  <>
                                    <ArrowPathIcon className="h-3 w-3" />
                                    Verifier
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date:</span>
                          <span>{formatDate(invoice.invoice_date)}</span>
                        </div>
                        {invoice.due_date && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Echeance:</span>
                            <span>{formatDate(invoice.due_date)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Montant HTVA:</span>
                          <span>{formatCurrency(invoice.total_htva)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">TVA:</span>
                          <span>{formatCurrency(invoice.total_tva)}</span>
                        </div>
                        <div className="flex justify-between font-medium text-gray-900 border-t pt-2">
                          <span>Total TVAC:</span>
                          <span className="text-primary">{formatCurrency(invoice.total_tvac)}</span>
                        </div>
                      </div>
                    </div>

                    {/* VAT re-verification result */}
                    {(validateVatMutation.isPending || vatCheckResult) && (
                      <>
                        <VatStatusSection
                          vatNumber={invoice.billing_vat_number}
                          isValidating={validateVatMutation.isPending}
                          vatValidationResult={vatCheckResult}
                        />
                        {vatCheckResult && !vatCheckResult.isValid && !vatCheckResult.errorType && (
                          <div className="bg-red-50 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-red-900">Numero de TVA non valide</h3>
                                <p className="text-sm text-red-700 mt-1">
                                  Le numero <span className="font-mono">{invoice.billing_vat_number}</span> n'est pas reconnu par VIES.
                                </p>
                                {vatCheckResult.error && (
                                  <p className="text-xs text-red-600 mt-2 break-words">{vatCheckResult.error}</p>
                                )}
                                <p className="text-xs text-red-600 mt-2">
                                  L'entreprise existe peut-etre mais n'est pas assujettie a la TVA, ou le numero contient une erreur. C'est probablement la cause du rejet par Falco.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Structured communication */}
                    {invoice.structured_communication && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-sm">
                          <BanknotesIcon className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-600">Communication structuree:</span>
                          <span className="font-mono font-medium text-indigo-600">{invoice.structured_communication}</span>
                        </div>
                      </div>
                    )}

                    {/* Payment details (PAID/SENT invoices) */}
                    {isPaidOrSent && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Paiement</h4>
                        {paymentsLoading ? (
                          <p className="text-sm text-gray-400">Chargement...</p>
                        ) : payments && payments.length > 0 ? (
                          <div className="space-y-3">
                            {payments.map((p) => (
                              <div key={`${p.payment_id}-${p.affaire_id}`} className="fact-detail-payment-row">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <span className="text-sm font-medium text-gray-900">{p.debtor_name}</span>
                                    {p.debtor_iban && (
                                      <span className="text-xs text-gray-400 ml-2 font-mono">{p.debtor_iban}</span>
                                    )}
                                  </div>
                                  <span className="text-sm font-semibold text-green-600">
                                    {formatCurrency(Number(p.amount_allocated))}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                                  <span>{formatDate(p.date)}</span>
                                  <span className="fact-detail-payment-method">
                                    {p.matching_method === 'STRUCTURED_COMM' ? 'Comm. structuree'
                                      : p.matching_method === 'AFFAIRE_LOOKUP' ? 'Via affaire'
                                      : p.matching_method === 'MANUAL' ? 'Manuel'
                                      : p.matching_method || '-'}
                                  </span>
                                  {p.is_validated && (
                                    <span className="fact-detail-payment-validated">Valide</span>
                                  )}
                                </div>
                              </div>
                            ))}
                            {payments.length > 0 && (
                              <div className="flex justify-between items-center border-t border-gray-200 pt-2 mt-2">
                                <span className="text-sm font-medium text-gray-700">Total recu</span>
                                <span className="text-sm font-semibold text-green-700">
                                  {formatCurrency(payments.reduce((sum, p) => sum + Number(p.amount_allocated), 0))}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : invoice.status === 'PAID' ? (
                          <p className="text-sm text-gray-500">Marque paye manuellement (aucun paiement lie)</p>
                        ) : (
                          <p className="text-sm text-gray-400">Aucun paiement lie</p>
                        )}
                      </div>
                    )}

                    {/* Delivery status (non-draft only) */}
                    {!isDraft && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Statut de livraison</h4>
                        <div className="space-y-3">
                          {/* Falco status */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <BuildingLibraryIcon className="h-4 w-4 text-gray-500" />
                              <span className="text-sm text-gray-600">Falco/Horus</span>
                            </div>
                            <span className={`text-sm font-medium ${
                              invoice.falco_status === 'SENT' || invoice.falco_status === 'UPLOADED'
                                ? 'text-green-600'
                                : invoice.falco_status === 'FAILED'
                                ? 'text-red-600'
                                : invoice.falco_status === 'PENDING' || invoice.falco_status === 'PROCESSING'
                                ? 'text-amber-600'
                                : 'text-gray-400'
                            }`}>
                              {invoice.falco_status || 'Non envoye'}
                            </span>
                          </div>

                          {/* Falco error details + resync action */}
                          {invoice.falco_status === 'FAILED' && (
                            <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-2">
                              <div className="flex items-start gap-2">
                                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <h5 className="text-sm font-medium text-red-900">Echec de transmission Falco</h5>
                                  {invoice.falco_error ? (
                                    <p className="text-xs text-red-700 mt-1 break-words font-mono">
                                      {invoice.falco_error}
                                    </p>
                                  ) : (
                                    <p className="text-xs text-red-700 mt-1">Aucun detail d'erreur disponible.</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <button
                                  onClick={handleResyncFalco}
                                  disabled={resyncFalcoMutation.isPending}
                                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {resyncFalcoMutation.isPending ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                                      Resync en cours...
                                    </>
                                  ) : (
                                    <>
                                      <ArrowPathIcon className="h-3.5 w-3.5" />
                                      Resynchroniser avec Falco
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Email status */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <EnvelopeIcon className="h-4 w-4 text-gray-500" />
                              <span className="text-sm text-gray-600">Email client</span>
                            </div>
                            <span className={`text-sm font-medium ${
                              invoice.email_sent ? 'text-green-600' : invoice.email_error ? 'text-red-600' : 'text-gray-400'
                            }`}>
                              {invoice.email_sent
                                ? `Envoye${invoice.email_recipient ? ` a ${invoice.email_recipient}` : ''}`
                                : invoice.email_error
                                ? 'Erreur'
                                : 'Non envoye'}
                            </span>
                          </div>

                          {/* Peppol status */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <GlobeAltIcon className="h-4 w-4 text-gray-500" />
                              <span className="text-sm text-gray-600">Peppol</span>
                            </div>
                            <span className={`text-sm font-medium ${
                              invoice.peppol_status === 'DELIVERED'
                                ? 'text-green-600'
                                : invoice.peppol_status === 'FAILED'
                                ? 'text-red-600'
                                : invoice.peppol_status === 'PENDING'
                                ? 'text-amber-600'
                                : 'text-gray-400'
                            }`}>
                              {invoice.peppol_status === 'NOT_REGISTERED'
                                ? 'Client non inscrit'
                                : invoice.peppol_status || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Email option (for validation) */}
                    {canValidate && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={sendEmailOnValidate}
                            onChange={(e) => setSendEmailOnValidate(e.target.checked)}
                            disabled={isValidating}
                            className="fact-email-checkbox"
                          />
                          <span className="text-sm text-gray-700">
                            Envoyer la facture par email au client lors de la validation
                          </span>
                        </label>

                        {/* Collapsible email editor */}
                        {sendEmailOnValidate && (
                          <div className="fact-email-editor">
                            {/* Language selector */}
                            <div className="fact-email-field">
                              <label className="fact-email-label" htmlFor="email-language">
                                Langue
                              </label>
                              <select
                                id="email-language"
                                value={emailLanguage}
                                onChange={(e) => handleLanguageChange(e.target.value)}
                                disabled={isValidating || isLoadingPreview}
                                className="fact-email-select"
                              >
                                {LANGUAGE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Recipient */}
                            <div className="fact-email-field">
                              <label className="fact-email-label" htmlFor="email-recipient">
                                Destinataire
                              </label>
                              <input
                                id="email-recipient"
                                type="email"
                                value={emailRecipient}
                                onChange={(e) => setEmailRecipient(e.target.value)}
                                disabled={isValidating}
                                placeholder="email@exemple.com"
                                className="fact-email-input"
                              />
                            </div>

                            {/* Subject (read-only, from API) */}
                            {emailSubject && (
                              <div className="fact-email-field">
                                <label className="fact-email-label">Objet</label>
                                <p className="fact-email-subject-preview">{emailSubject}</p>
                              </div>
                            )}

                            {/* Message textarea */}
                            <div className="fact-email-field">
                              <label className="fact-email-label" htmlFor="email-message">
                                Message
                              </label>
                              {isLoadingPreview ? (
                                <div className="fact-email-loading">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                                  <span>Chargement du modele...</span>
                                </div>
                              ) : (
                                <textarea
                                  id="email-message"
                                  value={emailMessage}
                                  onChange={(e) => {
                                    setEmailMessage(e.target.value);
                                    setUserEditedMessage(true);
                                  }}
                                  disabled={isValidating}
                                  rows={5}
                                  placeholder="Le message accompagnant la facture..."
                                  className="fact-email-textarea"
                                />
                              )}
                              {previewError && (
                                <p className="fact-email-error">{previewError}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* PDF preview button */}
                    <div className="flex justify-center">
                      <button
                        onClick={() => setShowPdfPreview(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <EyeIcon className="h-5 w-5" />
                        {isDraft ? 'Previsualiser le brouillon' : 'Voir le PDF'}
                      </button>
                    </div>

                    {/* Next step info (for draft/generated) */}
                    {canValidate && (
                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <PaperAirplaneIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                          <div>
                            <h3 className="font-medium text-blue-900">Prochaine etape</h3>
                            <p className="text-sm text-blue-700 mt-1">
                              {isDraft
                                ? 'Cliquez sur "Valider et envoyer" pour attribuer un numero, generer le PDF final et transmettre a Falco.'
                                : 'Cliquez sur "Valider et envoyer" pour transmettre la facture a Falco.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div className="flex gap-2">
                      {/* Download button */}
                      {(invoice.pdf_path || isDraft) && (
                        <button
                          onClick={handleDownloadPdf}
                          disabled={isDownloading}
                          className="facturation-btn-modal-secondary inline-flex items-center gap-2"
                        >
                          {isDownloading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                              Telechargement...
                            </>
                          ) : (
                            <>
                              <ArrowDownTrayIcon className="h-4 w-4" />
                              Telecharger
                            </>
                          )}
                        </button>
                      )}

                      {/* Cancel button */}
                      {canCancel && (
                        <button
                          onClick={handleCancel}
                          disabled={cancelMutation.isPending}
                          className="facturation-btn-modal-danger inline-flex items-center gap-2"
                        >
                          Annuler la facture
                        </button>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={onClose}
                        className="facturation-btn-modal-cancel"
                      >
                        Fermer
                      </button>

                      {/* Mark as paid button */}
                      {canMarkPaid && (
                        <button
                          onClick={handleMarkPaid}
                          disabled={markPaidMutation.isPending}
                          className="facturation-btn-modal-success inline-flex items-center gap-2"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                          Marquer payee
                        </button>
                      )}

                      {/* Validate button */}
                      {canValidate && (
                        <button
                          onClick={handleValidate}
                          disabled={isValidating}
                          className="facturation-btn-modal-primary inline-flex items-center gap-2"
                        >
                          {isValidating ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              <PaperAirplaneIcon className="h-4 w-4" />
                              Valider et envoyer
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* PDF Preview Modal */}
      {showPdfPreview && (
        <PdfPreviewModal
          document={{
            type: 'invoice',
            id: invoice.id,
            number: invoice.invoice_number || 'Brouillon',
            date: invoice.invoice_date,
            clientName: invoice.billing_name,
            amount: invoice.total_tvac,
            status: invoice.status,
            hasPdf: !!invoice.pdf_path || isDraft,
            billingVatNumber: invoice.billing_vat_number,
            structuredCommunication: invoice.structured_communication,
            falcoStatus: invoice.falco_status,
            falcoDocumentId: invoice.falco_document_id,
            falcoSyncedAt: invoice.falco_synced_at,
            falcoError: invoice.falco_error,
            peppolStatus: invoice.peppol_status,
            peppolDeliveredAt: invoice.peppol_delivered_at,
            peppolError: invoice.peppol_error,
            dueDate: invoice.due_date,
          }}
          onClose={() => setShowPdfPreview(false)}
          onDownload={handleDownloadPdf}
          previewMode={isDraft}
          onBack={() => setShowPdfPreview(false)}
          backLabel="Retour aux details"
        />
      )}
    </>
  );
};

export default InvoiceDetailModal;
